import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Modal,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import axiosInstance from "../../../API/axiosInstance";

type FormData = {
  sk: string;
  key: string;
};

const CredentialsInfo = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [isSuccessSnackbar, setIsSuccessSnackbar] = useState(false);

  const [open, setOpen] = useState(false);

  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    sk: "",
    key: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  /* ===================== FETCH CREDENTIALS ===================== */
  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        setIsLoading(true);
        const response = await ZyppLiteApi.api?.getCredentialsInfo();

        if (response?.data?.length > 0) {
          const row = response.data[0];
          setFormData({
            sk: row.sk || "",
            key: row.key || "",
          });
          setHasExistingCredentials(true); // 🔐 Read-only mode
        } else {
          setFormData({ sk: "", key: "" });
          setHasExistingCredentials(false);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setHasExistingCredentials(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCredentials();
  }, []);

  /* ===================== HANDLERS ===================== */
  const handleBackToDashboard = () => {
    navigate("/userdetails");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.sk.trim()) newErrors.sk = "Secret Key is required";
    if (!formData.key.trim()) newErrors.key = "Encryption Key is required";
    return newErrors;
  };

  /* ===================== CREATE ONLY ===================== */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsSubmitting(true);

      const payload = {
        sk: formData.sk,
        key: formData.key,
      };

      const response = await axiosInstance.post(
        "/users/dashboard/create-payment-credentials",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSnackbarMessage("Credentials created successfully!");
        setIsSuccessSnackbar(true);
        setOpen(true);

        setTimeout(() => {
          navigate("/userdetails", { state: { tabIndex: 1 } });
        }, 3000);
      }
    } catch (error) {
      console.error("Create error:", error);
      if (axios.isAxiosError(error)) {
        setSnackbarMessage(
          error.response?.data?.message || "Failed to create credentials"
        );
      } else {
        setSnackbarMessage("Network error");
      }
      setShowErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===================== LOADER ===================== */
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <CircularProgress />
      </div>
    );
  }

  /* ===================== UI ===================== */
  return (
    <div>
      <div className="flex bg-white min-h-80 box-shadow p-3 rounded-xl mx-16">
        <div className="w-full">
          <Grid container spacing={5} className="items-center">
            <Grid item md={6}>
              <div className="flex ml-4 items-center">
                <IconButton onClick={handleBackToDashboard}>
                  <ArrowBackIcon />
                </IconButton>
                <h2 className="ml-2 font-semibold text-2xl">
                  Create Credentials
                </h2>
              </div>
            </Grid>
          </Grid>

          <hr className="my-4" />

          <form onSubmit={handleSubmit} className="p-8">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Secret Key"
                  name="sk"
                  value={formData.sk}
                  onChange={handleChange}
                  error={!!errors.sk}
                  helperText={errors.sk}
                  variant="standard"
                  disabled={hasExistingCredentials}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Encryption Key"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  error={!!errors.key}
                  helperText={errors.key}
                  variant="standard"
                  disabled={hasExistingCredentials}
                />
              </Grid>
            </Grid>

            <Grid className="flex justify-end mt-5 " >
              <Button
                type="submit"
                disabled={isSubmitting || hasExistingCredentials}
                className={`${
                  hasExistingCredentials
                    ? "!bg-gray-400 !cursor-not-allowed"
                    : "!bg-black !text-white"
                }`}
                style={{
                  padding: "6px 28px",
                  borderRadius: "40px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {isSubmitting ? "Creating..." : "Create"}
              </Button>
            </Grid>
          </form>
        </div>
      </div>

      {/* ===================== SUCCESS MODAL ===================== */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 350,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              width: 60,
              height: 60,
              backgroundColor: "#00C853",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "auto",
            }}
          >
            <Typography sx={{ fontSize: 40, color: "#fff" }}>✔</Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Credentials Created Successfully!
          </Typography>

          <Button
            variant="contained"
            sx={{
              mt: 3,
              backgroundColor: "#000",
              borderRadius: "40px",
            }}
            onClick={() => navigate("/userdetails")}
          >
            Continue to Dashboard
          </Button>
        </Box>
      </Modal>

      {/* ===================== SNACKBAR ===================== */}
      <Snackbar
        open={showErrorSnackbar || isSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => {
          setShowErrorSnackbar(false);
          setIsSuccessSnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={isSuccessSnackbar ? "success" : "error"}
          sx={{ color: "#fff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CredentialsInfo;
